import { useContext } from "react";
import { Badge, Box, Button, Card, Flex, Table, Text } from "@chakra-ui/react";
import { ExternalLink, Wallet } from "lucide-react";

import Footer from "@/components/Footer";
import GenericTable from "@/components/GenericTable";
import { ORG_BILL_TABLE_COLUMNS } from "@/config/tables";
import TablesStateContext from "@/contexts/TablesStateContext";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import { usePortalContext } from "@/hooks/usePortalContext";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";

import CommonLayout from "../CommonLayout";
import OrgBillFilters from "./components/OrgBillFilters";
import { OrgQueryError } from "./components/OrgQueryError";
import { useOrgBills, type OrgBillRow } from "./hooks/useOrgBills";
import type { OrgBillFiltersType } from "./types/filters.type";

const PAYMENTS_PORTAL_URL = "https://pagos.palomma.com/palomma";

// Its own key, not the merchant screen's: a comercio search typed in org
// context should not follow the user into a building.
const TABLE_KEY = "org-bills";

const OrgBillingContainer = (): JSX.Element => {
  const { queryFilters, setQueryFilters } =
    useTableFiltersCursor<OrgBillFiltersType>(TABLE_KEY);
  const {
    rows,
    organization,
    totalOutstanding,
    isLoading,
    isError,
    errorCode,
    refetch,
  } = useOrgBills(queryFilters);
  const { setContext } = usePortalContext();
  const { resetAllTables } = useContext(TablesStateContext);
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");

  // The link is built from the bill's own `documentNumber`, never from the
  // comercio: it is a snapshot of the NIT taken when the bill was raised, so two
  // bills for one building may not carry the same one.
  const handlePay = (bill: OrgBillRow) => {
    const url = `${PAYMENTS_PORTAL_URL}?cliente=${encodeURIComponent(
      bill.merchantInfo.documentNumber
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // `resetAllTables()` first, or this table's filters leak into the building
  // being opened. No `navigate()`: the route is shared, so `ContextRoute` swaps
  // in `BillingContainer` on the next paint.
  const openMerchant = (merchantId: string) => {
    resetAllTables();
    setContext({ type: "merchant", id: merchantId });
  };

  const outstandingCard = (
    <Card.Root
      bg="white"
      rounded="lg"
      border="none"
      boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      p={4}
      maxW={{ base: "100%", md: "320px" }}
    >
      <Card.Body p={0} display="flex" flexDirection="column">
        <Flex justifyContent="space-between" alignItems="center" mb={1}>
          <Text fontSize="14px" fontWeight="600" color="#54596C">
            Saldo pendiente
          </Text>
          <Flex
            alignItems="center"
            justifyContent="center"
            w="32px"
            h="32px"
            bg="#F3F5FC"
            borderRadius="md"
          >
            <Wallet size={16} color="#A7ACB8" />
          </Flex>
        </Flex>
        <Text fontSize="24px" fontWeight="700" color="#1A202C">
          {formatCurrency(totalOutstanding)}
        </Text>
      </Card.Body>
    </Card.Root>
  );

  const table = (
    <Box display="flex" flexDirection="column" gap="1.5rem" flex="1" minH={0}>
      <OrgBillFilters
        queryFilters={queryFilters}
        setQueryFilters={setQueryFilters}
      />
      {isError ? (
        <OrgQueryError
          message="la facturación consolidada"
          code={errorCode}
          onRetry={() => void refetch()}
        />
      ) : (
        <>
          <GenericTable
            isLoading={isLoading}
            columns={ORG_BILL_TABLE_COLUMNS}
            fillHeight
          >
            {rows.map((bill) => (
              <Table.Row key={`${bill.merchantId}-${bill.id}`}>
                <Table.Cell>
                  <Text
                    color="palomma.600"
                    fontWeight="medium"
                    cursor="pointer"
                    truncate
                    _hover={{ textDecoration: "underline" }}
                    onClick={() => openMerchant(bill.merchantId)}
                  >
                    {bill.merchant}
                  </Text>
                </Table.Cell>
                <Table.Cell color="gray.600">{bill.period}</Table.Cell>
                <Table.Cell color="gray.600">{bill.dueDate}</Table.Cell>
                <Table.Cell>
                  <Badge
                    colorPalette={bill.type === "prepaid" ? "green" : "blue"}
                    variant="subtle"
                  >
                    {bill.type === "prepaid" ? "Descontada" : "Mes Vencido"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text fontWeight="bold" color="gray.800">
                    {formatCurrency(bill.amount)}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    colorPalette={bill.paid ? "green" : "orange"}
                    variant="subtle"
                  >
                    {bill.paid ? "Pagado" : "Sin Pagar"}
                  </Badge>
                </Table.Cell>
                <Table.Cell py={0}>
                  {!bill.paid && (
                    <Button
                      size="xs"
                      h="26px"
                      px={3}
                      colorPalette="palomma"
                      variant="outline"
                      fontWeight="medium"
                      gap={1}
                      onClick={() => handlePay(bill)}
                    >
                      <ExternalLink size={14} />
                      Pagar
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </GenericTable>
          <Box mt="auto" pt={6} flexShrink={0}>
            <Footer
              tableProps={{
                fileName: "facturacion-consolidada",
                items: rows,
                columns: ORG_BILL_TABLE_COLUMNS,
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <CommonLayout
      title="Facturación consolidada"
      subtitle={organization?.displayName}
      buttons={<></>}
      children={outstandingCard}
      table={table}
    />
  );
};

export default OrgBillingContainer;
